import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Clock, Calendar, ArrowLeft, Share2, Download, 
  Search, Video, TrendingUp, Target, 
  Heart, FileText, Globe, Mail, Zap, Users, DollarSign,
  BarChart3, CheckCircle, Sparkles, Award, Shield, ShieldCheck,
  Eye, Crosshair, PiggyBank, Cog, Brain, Repeat, Infinity,
  Gift, Building, Star, Calculator, Clock as ClockIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadMagnetModal from '@/components/LeadMagnetModal';
import RelatedPosts from '@/components/RelatedPosts';
import ReactionsBar from '@/components/ReactionsBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Share2, Search, Video, TrendingUp, Target, Heart, FileText,
  Globe, Mail, Zap, Users, DollarSign, BarChart3, CheckCircle, Sparkles,
  Award, Shield, ShieldCheck, Eye, Crosshair, PiggyBank, Cog, Brain,
  Repeat, Infinity, Gift, Building, Star, Calculator, Clock: ClockIcon
};

// Static gradient mapping - inline styles to bypass Tailwind purge issue
const gradientStyles = {
  'from-pink-500 via-purple-500 to-indigo-500': 'linear-gradient(135deg, #ec4899 0%, #a855f7 50%, #6366f1 100%)',
  'from-orange-500 via-red-500 to-pink-500': 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
  'from-green-500 via-teal-500 to-cyan-500': 'linear-gradient(135deg, #22c55e 0%, #14b8a6 50%, #06b6d4 100%)',
  'from-purple-500 via-pink-500 to-red-500': 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #ef4444 100%)',
  'from-blue-500 via-cyan-500 to-teal-500': 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 50%, #14b8a6 100%)',
  'from-indigo-500 via-purple-500 to-pink-500': 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
  'from-cyan-500 via-blue-500 to-indigo-500': 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #6366f1 100%)',
  'from-yellow-500 via-orange-500 to-red-500': 'linear-gradient(135deg, #eab308 0%, #f97316 50%, #ef4444 100%)',
  'from-green-500 via-emerald-500 to-teal-500': 'linear-gradient(135deg, #22c55e 0%, #10b981 50%, #14b8a6 100%)',
  'from-red-500 via-pink-500 to-purple-500': 'linear-gradient(135deg, #ef4444 0%, #ec4899 50%, #a855f7 100%)',
  'from-teal-500 to-cyan-500': 'linear-gradient(135deg, #14b8a6 0%, #06b6d4 100%)',
};

const getGradientStyle = (gradient) => {
  return gradientStyles[gradient] || gradientStyles['from-teal-500 to-cyan-500'];
};

const getIcon = (iconName) => {
  const Icon = iconMap[iconName] || Sparkles;
  return Icon;
};

// 3D Floating Elements Component
const FloatingElements = ({ iconName, gradient }) => {
  const Icon = getIcon(iconName);
  const gradientStyle = getGradientStyle(gradient);
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Large floating circle */}
      <div 
        className="absolute top-10 right-10 w-40 h-40 rounded-full opacity-20 blur-2xl"
        style={{ 
          background: gradientStyle,
          animation: 'float 6s ease-in-out infinite' 
        }}
      />
      
      {/* Medium floating shape */}
      <div 
        className="absolute bottom-20 left-20 w-32 h-32 rounded-full opacity-15 blur-xl"
        style={{ 
          background: gradientStyle,
          animation: 'float 8s ease-in-out infinite reverse' 
        }}
      />
      
      {/* Floating icons - 3D effect */}
      <div 
        className="absolute top-1/4 left-10"
        style={{ animation: 'float3d 5s ease-in-out infinite' }}
      >
        <div 
          className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl transform rotate-12"
          style={{ background: gradientStyle }}
        >
          <Icon className="w-8 h-8 text-white" />
        </div>
      </div>
      
      <div 
        className="absolute bottom-1/4 right-16"
        style={{ animation: 'float3d 7s ease-in-out infinite reverse' }}
      >
        <div 
          className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl transform -rotate-12"
          style={{ background: gradientStyle }}
        >
          <Icon className="w-10 h-10 text-white" />
        </div>
      </div>
      
      <div 
        className="absolute top-2/3 left-1/3"
        style={{ animation: 'float3d 9s ease-in-out infinite' }}
      >
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center shadow-xl transform rotate-45"
          style={{ background: gradientStyle }}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

// Chart Component
const BlogChart = ({ chartData }) => {
  if (!chartData || !chartData.data) return null;
  
  const colors = ['#18C0C0', '#EC4899', '#8B5CF6', '#F59E0B', '#3B82F6', '#10B981'];
  
  return (
    <div className="my-12 bg-gradient-to-br from-slate-50 to-teal-50 rounded-3xl p-8 border-2 border-teal-100" data-testid="blog-chart">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
          <BarChart3 className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900">{chartData.title}</h3>
      </div>
      
      <div style={{ width: '100%', height: 350 }}>
        <ResponsiveContainer>
          {chartData.type === 'bar' ? (
            <BarChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '2px solid #14b8a6',
                  borderRadius: '12px'
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || colors[index % colors.length]} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <LineChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '2px solid #14b8a6',
                  borderRadius: '12px'
                }}
              />
              <Legend />
              {Object.keys(chartData.data[0]).filter(k => k !== 'name').map((key, idx) => (
                <Line 
                  key={key}
                  type="monotone" 
                  dataKey={key} 
                  stroke={colors[idx % colors.length]} 
                  strokeWidth={3}
                  dot={{ r: 6, fill: colors[idx % colors.length] }}
                  activeDot={{ r: 8 }}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Benefits Component
const BenefitsSection = ({ benefits, gradient }) => {
  if (!benefits || benefits.length === 0) return null;
  const gradientStyle = getGradientStyle(gradient);
  
  return (
    <div className="my-12" data-testid="benefits-section">
      <h3 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
        <Sparkles className="w-8 h-8 text-teal-600" />
        Key Benefits for Your Business
      </h3>
      <div className="grid md:grid-cols-2 gap-6">
        {benefits.map((benefit, idx) => {
          const Icon = getIcon(benefit.icon);
          return (
            <div 
              key={idx}
              className="group relative bg-white border-2 border-slate-100 rounded-2xl p-6 hover:border-teal-300 hover:shadow-xl transition-all duration-300 overflow-hidden"
              data-testid={`benefit-${idx}`}
            >
              {/* Decorative gradient blob */}
              <div 
                className="absolute -top-10 -right-10 w-32 h-32 opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity"
                style={{ background: gradientStyle }}
              />
              
              <div className="relative">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg mb-4 group-hover:scale-110 transition-transform"
                  style={{ background: gradientStyle }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h4>
                <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Steps Component
const StepsSection = ({ steps, gradient }) => {
  if (!steps || steps.length === 0) return null;
  const gradientStyle = getGradientStyle(gradient);
  
  return (
    <div className="my-12" data-testid="steps-section">
      <h3 className="text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
        <CheckCircle className="w-8 h-8 text-teal-600" />
        Step-by-Step Process
      </h3>
      <div className="space-y-4">
        {steps.map((step, idx) => (
          <div 
            key={idx}
            className="group flex gap-6 bg-white border-2 border-slate-100 rounded-2xl p-6 hover:border-teal-300 hover:shadow-lg transition-all duration-300"
            data-testid={`step-${idx}`}
          >
            <div 
              className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg text-white text-2xl font-black group-hover:scale-110 transition-transform"
              style={{ background: gradientStyle }}
            >
              {step.step}
            </div>
            <div className="flex-1">
              <h4 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h4>
              <p className="text-slate-600 leading-relaxed">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Business Impact Stats
const BusinessImpactSection = ({ businessImpact, gradient }) => {
  if (!businessImpact || businessImpact.length === 0) return null;
  const gradientStyle = getGradientStyle(gradient);
  
  return (
    <div 
      className="my-12 rounded-3xl p-10 text-white relative overflow-hidden" 
      style={{ background: gradientStyle }}
      data-testid="business-impact"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative">
        <h3 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <TrendingUp className="w-8 h-8" />
          Business Impact We Deliver
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {businessImpact.map((impact, idx) => (
            <div key={idx} className="text-center" data-testid={`impact-${idx}`}>
              <div className="text-5xl font-black mb-2">{impact.metric}</div>
              <div className="text-sm font-medium opacity-90 uppercase tracking-wider">{impact.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Stats Section
const StatsSection = ({ stats }) => {
  if (!stats || stats.length === 0) return null;
  
  return (
    <div className="my-12 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-section">
      {stats.map((stat, idx) => (
        <div 
          key={idx}
          className="bg-white border-2 border-teal-100 rounded-2xl p-6 text-center hover:border-teal-300 hover:shadow-lg transition-all"
        >
          <div className="text-3xl font-black text-teal-600 mb-1">{stat.value}</div>
          <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
        </div>
      ))}
    </div>
  );
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const [magnetType, setMagnetType] = useState('social-media');

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
      } catch (error) {
        toast.error('Failed to load article');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug, API]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const openLeadMagnet = (type) => {
    setMagnetType(type);
    setShowLeadMagnet(true);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-slate-600">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Article Not Found</h1>
            <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-semibold">
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const gradient = blog.hero_gradient || 'from-teal-500 to-cyan-500';
  const iconName = blog.icon_name || 'TrendingUp';

  return (
    <div data-testid="blog-detail-page">
      <Header />

      {/* Hero Section with 3D Floating Elements */}
      <div 
        className="relative pt-32 pb-20 overflow-hidden"
        style={{ background: getGradientStyle(gradient) }}
      >
        <FloatingElements iconName={iconName} gradient={gradient} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white z-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white font-semibold mb-6"
            data-testid="back-to-blog-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Blog
          </Link>

          <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-bold mb-6 border border-white/30">
            {blog.category}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight" data-testid="blog-title">
            {blog.title}
          </h1>

          <p className="text-xl text-white/90 leading-relaxed mb-8 max-w-3xl">
            {blog.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-white/80">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{blog.reading_time}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(blog.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <button
              onClick={handleShare}
              className="ml-auto flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full font-semibold transition-colors border border-white/30"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <StatsSection stats={blog.stats} />
      </div>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Featured Image */}
        {blog.section_images && blog.section_images[0] && (
          <div className="my-8 rounded-3xl overflow-hidden shadow-2xl">
            <img 
              src={blog.section_images[0]} 
              alt={blog.title}
              className="w-full h-96 object-cover"
              data-testid="section-image-0"
            />
          </div>
        )}

        {/* Blog Content */}
        <div className="prose prose-lg max-w-none" data-testid="blog-content">
          {blog.content.split('\n\n').map((paragraph, index) => {
            const uniqueKey = `${blog.id}-para-${index}`;
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return (
                <h2 key={uniqueKey} className="text-3xl font-bold text-slate-900 mt-10 mb-4">
                  {paragraph.replace(/\*\*/g, '')}
                </h2>
              );
            } else if (paragraph.startsWith('**')) {
              const parts = paragraph.split('**');
              return (
                <p key={uniqueKey} className="text-lg text-slate-700 leading-relaxed mb-4">
                  {parts.map((part, i) => (i % 2 === 1 ? <strong key={`${uniqueKey}-${i}`} className="text-slate-900">{part}</strong> : part))}
                </p>
              );
            } else if (paragraph.startsWith('*') && paragraph.endsWith('*')) {
              return (
                <h3 key={uniqueKey} className="text-2xl font-bold text-teal-700 mt-6 mb-3">
                  {paragraph.replace(/\*/g, '')}
                </h3>
              );
            } else {
              return (
                <p key={uniqueKey} className="text-lg text-slate-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              );
            }
          })}
        </div>

        {/* Second section image */}
        {blog.section_images && blog.section_images[1] && (
          <div className="my-8 rounded-3xl overflow-hidden shadow-xl">
            <img 
              src={blog.section_images[1]} 
              alt="Illustration"
              className="w-full h-80 object-cover"
              data-testid="section-image-1"
            />
          </div>
        )}

        {/* Chart Visualization */}
        <BlogChart chartData={blog.chart_data} />

        {/* Benefits Section */}
        <BenefitsSection benefits={blog.benefits} gradient={gradient} />

        {/* Third section image */}
        {blog.section_images && blog.section_images[2] && (
          <div className="my-8 rounded-3xl overflow-hidden shadow-xl">
            <img 
              src={blog.section_images[2]} 
              alt="Illustration"
              className="w-full h-80 object-cover"
              data-testid="section-image-2"
            />
          </div>
        )}

        {/* Steps Section */}
        <StepsSection steps={blog.steps} gradient={gradient} />

        {/* Business Impact Section */}
        <BusinessImpactSection businessImpact={blog.business_impact} gradient={gradient} />

        {/* Emoji Reactions */}
        <ReactionsBar slug={blog.slug} />

        {/* Related Posts */}
        <RelatedPosts currentSlug={blog.slug} />

        {/* Lead Magnet CTA */}
        <div className="bg-gradient-to-br from-orange-50 to-teal-50 border-2 border-teal-200 rounded-3xl p-8 mt-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
              style={{ background: getGradientStyle(gradient) }}
            >
              <Download className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Want to Learn More?</h3>
              <p className="text-slate-700 mb-4">
                Download our free guide packed with actionable strategies to grow your business.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => openLeadMagnet('social-media')}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="download-social-media-btn"
                >
                  <Download className="w-4 h-4" />
                  Social Media Checklist
                </button>
                <button
                  onClick={() => openLeadMagnet('google-ads')}
                  className="bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="download-google-ads-btn"
                >
                  <Download className="w-4 h-4" />
                  Google Ads Guide
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div 
          className="rounded-3xl p-10 mt-12 text-center text-white relative overflow-hidden"
          style={{ background: getGradientStyle(gradient) }}
        >
          <div className="absolute inset-0 opacity-20">
            <FloatingElements iconName={iconName} gradient={gradient} />
          </div>
          <div className="relative">
            <h3 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h3>
            <p className="mb-6 text-white/90 text-lg max-w-2xl mx-auto">
              Let's discuss how we can help you achieve your digital marketing goals with proven strategies and measurable results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9068922387"
                className="inline-block bg-white text-slate-900 px-8 py-3 rounded-full font-bold hover:bg-slate-100 transition-colors"
                data-testid="blog-cta-call-btn"
              >
                Call Us: 9068922387
              </a>
              <a
                href="mailto:ashvyadigitalmedia@gmail.com"
                className="inline-block bg-white/20 backdrop-blur-sm border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white/30 transition-colors"
                data-testid="blog-cta-email-btn"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </article>

      <LeadMagnetModal
        isOpen={showLeadMagnet}
        onClose={() => setShowLeadMagnet(false)}
        magnetType={magnetType}
      />

      <div className="h-20"></div>
      <Footer />

      {/* Add CSS animations for 3D floating elements */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes float3d {
          0%, 100% { 
            transform: translateY(0) translateX(0) rotate(12deg) scale(1);
            filter: drop-shadow(0 10px 20px rgba(0,0,0,0.15));
          }
          25% { 
            transform: translateY(-15px) translateX(10px) rotate(20deg) scale(1.05);
          }
          50% { 
            transform: translateY(-30px) translateX(-5px) rotate(0deg) scale(1.1);
            filter: drop-shadow(0 20px 30px rgba(0,0,0,0.2));
          }
          75% { 
            transform: translateY(-15px) translateX(-10px) rotate(-10deg) scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default BlogDetail;
